import React, { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';

import { Routes } from '@common/constants';
import { BasketDrawerConnected } from '@common/components/BasketDrawer';
import { useRouter } from 'shared-routing';
import { useTranslations } from '@lang';
import { useGetSpaTreatment, useGetSpaSettings } from '@features/spa/hooks';
import { parseDateToString, mergeCrossSellWithUpsell } from '@utils/helpers';
import { getPriceForMinimumGuests } from '@utils/getPriceForMinimumGuests';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';

import { SpaTreatmentFacilityDetailTemplate, Recommendations, RecommendationItem } from 'voyager';

import { SpaTreatmentDetailV2Props } from './SpaTreatmentDetailV2.types';
import { SpaTreatmentBookingV2 } from './SpaTreatmentBookingV2';
import { SpaTreatmentExtrasV2 } from './SpaTreatmentExtrasV2';
import { SpaTreatmentFormValues } from './SpaTreatmentBookingV2.types';
import { spaEntityFormSchema } from './SpaTreatmentForm.schema';

export const SpaTreatmentDetailV2: FC<SpaTreatmentDetailV2Props> = ({
  id,
  bookingStep,
  property,
  language,
  journeyParams,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);

  const journeyBar = <JourneyPickerV2Connected dateMode="single" dateContext="spa" showGuests />;

  const { data: spaEntity, isFetching } = useGetSpaTreatment(
    property.id,
    language,
    id,
    parseDateToString(journeyParams.spaDate)
  );

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const termsAndConditions = spaSettings?.termsAndConditions || '';
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const minGuests = spaEntity?.minimumGuests || 1;
  const maxGuests = spaEntity?.maximumGuests || minGuests;
  const requestedGuests = journeyParams.adults || 1;
  const initialGuestCount = Math.max(minGuests, Math.min(maxGuests, requestedGuests));

  const form = useForm<SpaTreatmentFormValues>({
    resolver: zodResolver(spaEntityFormSchema),
    defaultValues: {
      date: moment(journeyParams.spaDate).format('YYYY-MM-DD'),
      guests: Array.from({ length: initialGuestCount }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender:
          spaEntity?.genderSpecific === 'M'
            ? 'male'
            : spaEntity?.genderSpecific === 'F'
            ? 'female'
            : '',
      })),
    },
    mode: 'onChange',
  });

  const handleBack = () => {
    router.push({ pathname: Routes.SPA_TREATMENT_LIST });
  };

  const handleReserve = () => {
    router.push({
      pathname: Routes.SPA_TREATMENT_BOOKING,
      query: { id, bookingStep: 'availability' },
    });
  };

  const handleCloseModal = useCallback(
    (showMiniBasket = false) => {
      router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
      if (showMiniBasket) {
        setIsBasketDrawerOpen(true);
      }
    },
    [router, id]
  );

  const mergedItems = mergeCrossSellWithUpsell(
    spaEntity?.upSell,
    'spa-treatment',
    spaEntity?.crossSellObjects
  );

  const recommendations: RecommendationItem[] = (mergedItems || []).map((item: any) => ({
    id: String(item.id),
    name: item.name,
    imageUrl: item.image?.url,
    price:
      item.price?.amount != null
        ? { amount: item.price.amount, currencyCode: item.price.currencyCode }
        : undefined,
  }));

  const handleSelectRecommendation = (recId: string) => {
    router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id: recId } });
  };

  const price = spaEntity
    ? getPriceForMinimumGuests(spaEntity.price, spaEntity.minimumGuests)
    : undefined;

  const durationLabel = spaEntity?.displayDuration ? `${spaEntity.displayDuration} min` : undefined;

  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  return (
    <>
      {!bookingStep && (
        <SpaTreatmentFacilityDetailTemplate
          imageUrl={spaEntity?.image?.url}
          imageAlt={spaEntity?.name}
          title={spaEntity?.name || ''}
          price={
            price?.amount != null
              ? { amount: price.amount, currencyCode: price.currencyCode }
              : undefined
          }
          priceLabel={t('common.from')}
          categoryLabel={durationLabel}
          description={spaEntity?.description}
          termsContent={
            spaEntity?.termsAndConditions || termsAndConditions ? (
              <p>{spaEntity?.termsAndConditions || termsAndConditions}</p>
            ) : undefined
          }
          termsLabel={t('app.termsAndConditions')}
          onReserve={spaEntity ? handleReserve : undefined}
          reserveLabel={t('spa.book')}
          onBack={handleBack}
          isLoading={isFetching && !spaEntity}
          journeyBar={journeyBar}
          recommendations={
            recommendations.length > 0 ? (
              <Recommendations
                items={recommendations}
                onSelect={handleSelectRecommendation}
                title={t('spa.headingColumnTitle')}
                fromPriceLabel={t('common.from')}
                noImageLabel={t('spa.noImage')}
              />
            ) : undefined
          }
        />
      )}
      {bookingStep === 'availability' && spaEntity && (
        <FormProvider {...form}>
          <SpaTreatmentBookingV2
            id={id}
            property={property}
            language={language}
            onClose={handleCloseModal}
          />
        </FormProvider>
      )}
      {bookingStep === 'extras' && spaEntity && (
        <FormProvider {...form}>
          <SpaTreatmentExtrasV2
            spaEntity={spaEntity}
            property={property}
            language={language}
            id={id}
            onClose={handleCloseModal}
            onBack={() =>
              router.push({
                pathname: Routes.SPA_TREATMENT_BOOKING,
                query: { id, bookingStep: 'availability' },
              })
            }
          />
        </FormProvider>
      )}
      <BasketDrawerConnected
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </>
  );
};
